import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, X, Play, Pause,
  LayoutDashboard, ShoppingCart, Package, Boxes, FileText,
  Users, BarChart3, Truck, Printer, Shield
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const SLIDES = [
  {
    id: 'dashboard', icon: LayoutDashboard, image: '/demo/01-dashboard.png',
    en: {
      title: 'Dashboard', subtitle: 'Your Shop Command Centre',
      description: 'Get a real-time overview of your shop. Track today\'s revenue, total sales, product count and low stock alerts \u2014 all in one glance.',
      tip: 'Check your dashboard each morning to plan the day ahead.',
    },
    es: {
      title: 'Panel de Control', subtitle: 'Tu Centro de Mando',
      description: 'Obt\u00E9n una vista en tiempo real de tu tienda. Sigue los ingresos del d\u00EDa, ventas totales, productos y alertas de stock bajo \u2014 todo de un vistazo.',
      tip: 'Revisa tu panel cada ma\u00F1ana para planificar el d\u00EDa.',
    },
  },
  {
    id: 'pos', icon: ShoppingCart, image: '/demo/02-pos-product-grid.png',
    en: {
      title: 'Point of Sale', subtitle: 'Fast & Intuitive POS',
      description: 'Process orders quickly with our touch-friendly POS. Browse categories, add items, handle cash & card payments and print receipts \u2014 all optimised for speed. Works offline so you never miss a sale.',
      tip: 'Use the USB barcode scanner or camera scanner for instant product lookup.',
    },
    es: {
      title: 'Punto de Venta', subtitle: 'TPV R\u00E1pido e Intuitivo',
      description: 'Procesa pedidos r\u00E1pidamente con nuestro TPV t\u00E1ctil. Navega categor\u00EDas, a\u00F1ade art\u00EDculos, gestiona pagos en efectivo y tarjeta e imprime recibos \u2014 todo optimizado para la velocidad. Funciona sin conexi\u00F3n.',
      tip: 'Usa el esc\u00E1ner de c\u00F3digo de barras USB o la c\u00E1mara para buscar productos al instante.',
    },
  },
  {
    id: 'products', icon: Package, image: '/demo/06-products.png',
    en: {
      title: 'Products', subtitle: 'Manage Your Product Catalog',
      description: 'Add products with images, prices, barcodes, tax rates and stock levels. Import in bulk via CSV, scan barcodes with your camera, and print barcode labels for any product.',
      tip: 'Use the Camera button to scan barcodes directly from your phone or tablet.',
    },
    es: {
      title: 'Productos', subtitle: 'Gestiona Tu Cat\u00E1logo de Productos',
      description: 'A\u00F1ade productos con im\u00E1genes, precios, c\u00F3digos de barras, tasas de IVA y niveles de stock. Importa en masa via CSV, escanea c\u00F3digos con la c\u00E1mara e imprime etiquetas de c\u00F3digos de barras.',
      tip: 'Usa el bot\u00F3n de C\u00E1mara para escanear c\u00F3digos de barras directamente desde tu m\u00F3vil o tablet.',
    },
  },
  {
    id: 'stock', icon: Boxes, image: '/demo/07-stock-management.png',
    en: {
      title: 'Stock Management', subtitle: 'Track Your Inventory',
      description: 'Monitor stock levels for all tracked items. Get alerts when items run low. Edit quantities inline or in bulk, use the AI Receipt Scanner to add stock automatically, and view reorder suggestions.',
      tip: 'Set low-stock alerts to avoid running out during busy hours.',
    },
    es: {
      title: 'Gesti\u00F3n de Stock', subtitle: 'Controla Tu Inventario',
      description: 'Monitorea los niveles de stock. Recibe alertas cuando los art\u00EDculos est\u00E9n bajos. Edita cantidades individualmente o en masa, usa el Esc\u00E1ner IA de Recibos y consulta sugerencias de reposici\u00F3n.',
      tip: 'Configura alertas de stock bajo para no quedarte sin stock en horas punta.',
    },
  },
  {
    id: 'invoices', icon: FileText, image: '/demo/08-invoices.png',
    en: {
      title: 'Invoices', subtitle: 'VeriFactu-Compliant Invoicing',
      description: 'Create and manage invoices that comply with Spain\'s VeriFactu regulations. Every invoice includes a cryptographic hash chain, QR code, and is automatically submitted to AEAT. Download PDFs and email them to clients.',
      tip: 'Use recurring invoices for regular clients to save time each month.',
    },
    es: {
      title: 'Facturas', subtitle: 'Facturaci\u00F3n Conforme a VeriFactu',
      description: 'Crea y gestiona facturas conformes con la normativa VeriFactu de Espa\u00F1a. Cada factura incluye cadena de hash criptogr\u00E1fica, c\u00F3digo QR y se env\u00EDa autom\u00E1ticamente a la AEAT. Descarga PDFs y env\u00EDalos por email.',
      tip: 'Usa facturas recurrentes para clientes habituales y ahorra tiempo cada mes.',
    },
  },
  {
    id: 'staff', icon: Users, image: '/demo/11-staff-management.png',
    en: {
      title: 'Staff Management', subtitle: 'Manage Your Team',
      description: 'Add staff members with role-based access \u2014 managers, cashiers, stock clerks and drivers. Each role sees only the sections they need. Use 4-digit PIN codes for quick POS staff login on shared terminals.',
      tip: 'Create a manager account so you can delegate without sharing your admin login.',
    },
    es: {
      title: 'Gesti\u00F3n de Personal', subtitle: 'Gestiona Tu Equipo',
      description: 'A\u00F1ade miembros del personal con acceso por roles \u2014 gerentes, cajeros, almac\u00E9n y repartidores. Cada rol ve solo las secciones que necesita. Usa c\u00F3digos PIN de 4 d\u00EDgitos para un inicio r\u00E1pido en el TPV.',
      tip: 'Crea una cuenta de gerente para delegar sin compartir tu login de administrador.',
    },
  },
  {
    id: 'reports', icon: BarChart3, image: '/demo/09-reports.png',
    en: {
      title: 'Reports', subtitle: 'Daily Sales Summary & Analytics',
      description: 'Review your daily performance \u2014 total orders, revenue, average sale value. See breakdowns by payment method and hourly sales charts. Filter by any time period (today, week, month, year, custom range) and export to CSV.',
      tip: 'Review this report every night before closing to catch any discrepancies.',
    },
    es: {
      title: 'Informes', subtitle: 'Resumen de Ventas y An\u00E1lisis',
      description: 'Revisa tu rendimiento diario \u2014 pedidos totales, ingresos, valor promedio de venta. Ve desgloses por m\u00E9todo de pago y gr\u00E1ficos de ventas por hora. Filtra por periodo y exporta a CSV.',
      tip: 'Revisa este informe cada noche antes de cerrar para detectar discrepancias.',
    },
  },
  {
    id: 'delivery', icon: Truck, image: '/demo/12-delivery.png',
    en: {
      title: 'Delivery', subtitle: 'Manage Orders & Drivers',
      description: 'Handle delivery orders from start to finish. Add drivers, assign orders, and track delivery status (pending, assigned, picked up, delivered). View revenue and driver availability at a glance.',
      tip: 'Use status filters to quickly find orders that need attention.',
    },
    es: {
      title: 'Entregas', subtitle: 'Gestiona Pedidos y Repartidores',
      description: 'Gestiona pedidos de entrega de principio a fin. A\u00F1ade repartidores, asigna pedidos y sigue el estado (pendiente, asignado, recogido, entregado). Ve ingresos y disponibilidad de repartidores de un vistazo.',
      tip: 'Usa los filtros de estado para encontrar r\u00E1pidamente los pedidos que necesitan atenci\u00F3n.',
    },
  },
  {
    id: 'printer', icon: Printer, image: null,
    en: {
      title: 'Printer Settings', subtitle: 'Connect Your Printers',
      description: 'Set up thermal printers for receipts. Connect via Network, Bluetooth, or Direct USB. Configure auto-printing for receipts and cash drawer opening. Print barcode labels for your products.',
      tip: 'Network printing is the most reliable option for busy shops.',
    },
    es: {
      title: 'Impresora', subtitle: 'Conecta Tus Impresoras',
      description: 'Configura impresoras t\u00E9rmicas para recibos. Conecta via Red, Bluetooth o USB directo. Configura la impresi\u00F3n autom\u00E1tica de recibos y apertura del caj\u00F3n. Imprime etiquetas de c\u00F3digos de barras.',
      tip: 'La impresi\u00F3n por red es la opci\u00F3n m\u00E1s fiable para tiendas con mucho volumen.',
    },
  },
  {
    id: 'verifactu', icon: Shield, image: '/demo/10-verifactu-dashboard.png',
    en: {
      title: 'VeriFactu', subtitle: 'Spain Tax Compliance (Anti-Fraud)',
      description: 'Configure your fiscal data \u2014 NIF, legal name, and invoice series. VeriFactu ensures all your invoices comply with Spain\'s anti-fraud regulations. Monitor submission status, chain integrity, and success rate from the dashboard.',
      tip: 'Enter your data exactly as registered with AEAT to avoid compliance issues.',
    },
    es: {
      title: 'VeriFactu', subtitle: 'Cumplimiento Fiscal de Espa\u00F1a (Antifraude)',
      description: 'Configura tus datos fiscales \u2014 NIF, raz\u00F3n social y serie de factura. VeriFactu asegura que todas tus facturas cumplan con la normativa antifraude. Monitorea el estado de env\u00EDo, integridad de cadena y tasa de \u00E9xito.',
      tip: 'Introduce tus datos exactamente como est\u00E1n registrados en AEAT para evitar problemas.',
    },
  },
];

const TourGuide = ({ onClose }) => {
  const { language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lang, setLang] = useState(language?.startsWith('es') ? 'es' : 'en');
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const slide = SLIDES[currentSlide];
  const content = slide[lang];
  const Icon = slide.icon;
  const progress = ((currentSlide + 1) / SLIDES.length) * 100;

  const next = useCallback(() => {
    if (currentSlide < SLIDES.length - 1) {
      setCurrentSlide(s => s + 1);
      setImageLoaded(false);
    }
  }, [currentSlide]);

  const prev = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(s => s - 1);
      setImageLoaded(false);
    }
  }, [currentSlide]);

  // Auto-play
  useEffect(() => {
    if (!isPlaying) return;
    if (currentSlide >= SLIDES.length - 1) { setIsPlaying(false); return; }
    const timer = setTimeout(next, 6000);
    return () => clearTimeout(timer);
  }, [isPlaying, currentSlide, next]);

  // Keyboard navigation
  useEffect(() => {
    const handle = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [next, prev, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tour-guide-overlay">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="border-b bg-slate-50 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#22c55e] flex items-center justify-center">
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">{content.title}</p>
              <p className="text-xs text-slate-500">{currentSlide + 1} / {SLIDES.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setLang('en')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${lang === 'en' ? 'bg-[#22c55e] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid="tour-lang-en"
              >EN</button>
              <button
                onClick={() => setLang('es')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${lang === 'es' ? 'bg-[#22c55e] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                data-testid="tour-lang-es"
              >ES</button>
            </div>
            <button onClick={() => setIsPlaying(!isPlaying)}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors" data-testid="tour-autoplay">
              {isPlaying ? <Pause className="w-4 h-4 text-slate-600" /> : <Play className="w-4 h-4 text-slate-600" />}
            </button>
            <button onClick={onClose}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors" data-testid="tour-close">
              <X className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div className="h-full bg-[#22c55e] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Screenshot */}
          <div className="bg-slate-900 relative flex items-center justify-center" style={{ minHeight: '220px' }}>
            {slide.image && (
              <>
                {!imageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 border-3 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <img
                  src={slide.image}
                  alt={content.title}
                  className={`max-h-[45vh] w-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImageLoaded(true)}
                />
              </>
            )}
            {!slide.image && (
              <div className="py-16 flex flex-col items-center gap-3 text-white/30">
                <Icon className="w-16 h-16" />
                <p className="text-sm">{content.title}</p>
              </div>
            )}
            {/* Subtitle overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-6 py-4">
              <p className="text-white text-lg font-bold">{content.subtitle}</p>
            </div>
          </div>

          {/* Description */}
          <div className="px-6 py-5 space-y-3">
            <p className="text-slate-700 text-sm leading-relaxed">{content.description}</p>
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <p className="text-[#22c55e] font-bold text-sm mb-0.5">TIP</p>
              <p className="text-green-800 text-sm">{content.tip}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-3 bg-slate-50 flex items-center justify-between flex-shrink-0">
          {/* Slide dots */}
          <div className="flex gap-1.5 max-w-[60%] overflow-x-auto">
            {SLIDES.map((s, i) => {
              const DotIcon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => { setCurrentSlide(i); setImageLoaded(false); }}
                  className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                    i === currentSlide
                      ? 'bg-[#22c55e] text-white shadow-sm'
                      : i < currentSlide
                        ? 'bg-green-100 text-[#22c55e]'
                        : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                  }`}
                  data-testid={`tour-dot-${s.id}`}
                >
                  <DotIcon className="w-3.5 h-3.5" />
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              disabled={currentSlide === 0}
              className="px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-md text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              data-testid="tour-prev"
            >
              <ChevronLeft className="w-4 h-4" />
              {lang === 'es' ? 'Anterior' : 'Previous'}
            </button>
            {currentSlide < SLIDES.length - 1 ? (
              <button
                onClick={next}
                className="px-3 py-1.5 text-sm font-medium bg-[#22c55e] text-white rounded-md hover:bg-[#16a34a] flex items-center gap-1"
                data-testid="tour-next"
              >
                {lang === 'es' ? 'Siguiente' : 'Next'}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-sm font-medium bg-[#1e3a5f] text-white rounded-md hover:bg-[#2a4f7f] flex items-center gap-1"
                data-testid="tour-finish"
              >
                {lang === 'es' ? 'Empezar' : 'Get Started'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourGuide;
